import { FunctionComponent, useState } from "react"
import Sidebar from "../features/sidebar/components/sidebar"
import { useTypedSelector } from "../app/store"
import { useSendSupportMessageMutation } from "../app/services/meseeknaApiV2"

const Support: FunctionComponent = () => {
  const user = useTypedSelector((state) => state.authSlice.user)

  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const [sendSupportMessage] = useSendSupportMessageMutation()

  const handleChatClick = () => {
    if (
      "BrevoConversations" in window &&
      typeof window["BrevoConversations"] === "function"
    ) {
      window.BrevoConversations("show")
      window.BrevoConversations("openChat", true)
      window.BrevoConversations("startBotScenario", "xCYnqADFQyvHygy3c")
    }
  }

  const hasLiveChat = "BrevoConversations" in window

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError("")
    setSubmitSuccess(false)

    try {
      await sendSupportMessage({
        subject,
        message,
        userEmail: user?.email || "",
        userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
      }).unwrap()

      setSubmitSuccess(true)
      setSubject("")
      setMessage("")
    } catch (error) {
      setSubmitError("Failed to send message. Please try again or contact us directly.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-gray-50 min-h-screen flex">
      <Sidebar />
      <div className="flex-1 p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-gray-900">Support</h1>
            <p className="text-lg text-gray-500 mt-3">Get help with Meseekna or contact our support team.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Help Information Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-[#4d86ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Help & Resources</h2>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-base font-medium text-gray-900 mb-1">Getting Started</h3>
                  <p className="text-base text-gray-500">
                    If you&apos;re new to Meseekna, start by exploring the dashboard and familiarizing yourself with the simulation interface.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-base font-medium text-gray-900 mb-1">Technical Issues</h3>
                  <p className="text-base text-gray-500">
                    If you&apos;re experiencing technical difficulties, try refreshing the page or clearing your browser cache.
                  </p>
                </div>

                {hasLiveChat && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h3 className="text-base font-medium text-gray-900 mb-1">Live Chat</h3>
                    <p className="text-base text-gray-500 mb-3">
                      Need immediate assistance? Start a live chat with our support team.
                    </p>
                    <button
                      onClick={handleChatClick}
                      className="px-4 py-2 bg-[#4d86ff] text-white rounded-lg hover:bg-[#3d76ef] transition-colors text-base font-medium"
                    >
                      Start Live Chat
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Form Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-[#8647a1]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Contact Support</h2>
              </div>

              <p className="text-base text-gray-500 mb-4">
                Send us a message and we&apos;ll get back to you as soon as possible.
              </p>

              {submitSuccess && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">
                    Your message has been sent successfully! We&apos;ll get back to you soon.
                  </p>
                </div>
              )}

              {submitError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{submitError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4d86ff] focus:border-[#4d86ff] outline-none transition-colors"
                    placeholder="What do you need help with?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4d86ff] focus:border-[#4d86ff] outline-none transition-colors resize-none"
                    placeholder="Describe your issue or question in detail..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !subject || !message}
                  className="w-full px-4 py-2.5 bg-[#4d86ff] text-white rounded-lg hover:bg-[#3d76ef] transition-colors text-base font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>

              <p className="mt-4 text-xs text-gray-400 text-center">
                We typically respond within 24 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Support
